import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CircularProgress,
  Alert,
  Box,
  Typography,
  Button,
} from "@mui/material"; // For loading/error

// API Functions
import { getServiceById, getServices } from "../api/serviceApi"; // Use Service API

// Components
import ProductTabs from "../components/ProductTabs";
import ImageMagnifier from "../components/ImageMagnifier";
import Accordion from "../components/Accordion";
import ImageGallery from "../components/ImageGallery";
import TechnicalSpecifications from "../components/TechnicalSpecifications";
import MainScrollText from "../components/MainScrollText";
import ContactForm from "../components/ContactForm";
import ServiceSlider from "../components/ServiceSlider";
// Removed: import useStore from "../context/store";

function ServiceDetail() {
  const { serviceId } = useParams();
  const [service, setService] = useState(null);
  const [relativeServices, setRelativeServices] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null); // Initialize as null
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!serviceId) {
      setError("Service ID is missing.");
      setLoading(false);
      return;
    }

    const fetchServiceDetails = async () => {
      setLoading(true);
      setError(null);
      setService(null); // Reset previous service
      setRelativeServices([]);

      try {
        // Fetch the specific service
        const serviceResponse = await getServiceById(serviceId);
        const fetchedService = serviceResponse.data;

        if (!fetchedService) {
          throw new Error("Service not found.");
        }

        setService(fetchedService);
        setCurrentPlan(fetchedService.specificationsDefault); // Set initial plan

        // Fetch all services to find related ones
        try {
          const allServicesResponse = await getServices();
          const related = allServicesResponse.data.filter(
            (item) =>
              item.sector === fetchedService.sector &&
              item._id !== fetchedService._id // Exclude self
          );
          setRelativeServices(related);
        } catch (allServicesError) {
          console.error("Error fetching related services:", allServicesError);
          setRelativeServices([]);
        }
      } catch (err) {
        console.error("Fetch Service Detail Error:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to fetch service details."
        );
        setService(null);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceDetails();
  }, [serviceId]); // Re-run effect if serviceId changes

  // --- Render Loading State ---
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // --- Render Error State ---
  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button component={Link} to="/services" sx={{ mt: 2 }}>
          Back to Services
        </Button>
      </Box>
    );
  }

  // --- Render Not Found State ---
  if (!service) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Service not found.</Typography>
        <Button component={Link} to="/services" sx={{ mt: 2 }}>
          Back to Services
        </Button>
      </Box>
    );
  }

  // --- Render Service Details ---
  return (
    <div>
      {/* --- Banner Section --- */}
      <div
        style={{ backgroundImage: `url(${service.bannerImageUrl})` }}
        className=" bg-cover bg-center background-no-repeat background-blend-multiply pt-24 md:pt-48 pb-14 md:pb-28 px-4 md:px-0"
      >
        <div className="max-w-[1400px] mx-auto flex  h-full flex-col ">
          <div className="products-page">
            <h1 className="text-white md:text-8xl text-4xl text-left font-medium">
              {service.title}
            </h1>
          </div>
          <div className="filters text-left space-y-5 mt-10 max-w-4xl">
            <h2 className="text-white md:text-2xl md:pb-14">
              {service.hightLight_text}
            </h2>
            <div className="flex space-x-4 justify-end md:justify-start">
              <Link to="/contact">
                <button className="bg-[#cc673d] text-white border-[#cc673d]">
                  Contact Us
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* --- ProductTabs (Consider renaming if used only here, or make generic) --- */}
      <ProductTabs />

      {/* --- Overview Section --- */}
      <section
        id="overview"
        className="flex flex-col md:flex-row justify-between pt-6 md:pt-32 md:pb-10 bg-white md:gap-20"
      >
        <div className="w-full md:w-1/2 overflow-hidden">
          {service.bannerImageUrl && (
            <ImageMagnifier src={service.bannerImageUrl} zoom={3} />
          )}
        </div>
        <div className="w-full md:w-1/2 md:pl-6 md:pt-0 pt-8 p-4 md:mr-8">
          <h2 className="text-blue-800 font-bold text-lg uppercase mb-4">
            Overview
          </h2>
          <h1 className="text-3xl font-bold mb-4">{service.title}</h1>
          <div className="py-10">
            {/* Check if description is an array */}
            {Array.isArray(service.description)
              ? service.description.map((item, i) => (
                  <p
                    className="text-gray-700 mb-4"
                    key={i}
                    dangerouslySetInnerHTML={{ __html: item }}
                  />
                ))
              : null}
          </div>
          <Link to="/contact">
            <button className="bg-[#cc673d] px-4 py-2 flex items-center hover:bg-[#e27a4d] transition text-white">
              Contact Us
            </button>
          </Link>
        </div>
      </section>

      {/* --- ImageGallery --- */}
      {service.images && service.images.length > 0 && (
        <section id="images" className="md:py-16 py-8">
          <ImageGallery images={service.images} />
        </section>
      )}

      {/* --- Service Plan Section --- */}
      {service.specifications &&
        Object.keys(service.specifications).length > 0 && (
          <section id="specifications" className="md:py-16 md:ml-10">
            <h2 className="text-3xl font-semibold py-6 px-4 md:px-0">
              Service Plan
            </h2>
            <TechnicalSpecifications
              specifications={service.specifications}
              default1={service.specificationsDefault}
              selectedSpec={currentPlan} // Use state variable
              onSpecChange={setCurrentPlan}
            />
          </section>
        )}

      {/* --- MainScrollText --- */}
      {service.mainScrollText && (
        <div>
          <section className="bg-black">
            <MainScrollText data={service.mainScrollText} />
          </section>
        </div>
      )}

      {/* --- FAQ Section --- */}
      {service.faq && service.faqList && service.faqList.length > 0 && (
        <section
          id="faq's"
          className="max-w-[1400px] mx-auto flex h-full py-20 flex-col md:flex-row w-full px-4 md:px-0"
        >
          <div className="md:w-1/2 space-y-8 mb-10 md:mb-0">
            <h1 className="text-4xl font-semibold">Your questions, answered</h1>
            {service.faq.title && (
              <h2 className="text-2xl">{service.faq.title}</h2>
            )}
            {service.faq.link && service.faq.label && (
              <div>
                <Link to={service.faq.link}>
                  <button className="bg-[#cc673d] px-4 py-2 flex items-center hover:bg-[#e27a4d] transition text-white">
                    {service.faq.label}
                  </button>
                </Link>
              </div>
            )}
          </div>
          <div className="md:w-1/2">
            <Accordion data={service.faqList} />
          </div>
        </section>
      )}

      {/* --- Service Guide Section --- */}
      {service.service_guid && service.service_guid.video && (
        <section className="max-w-[1400px] mx-auto h-full md:py-20 ">
          <div className="px-4 md:px-0">
            <h2 className="text-blue-800 font-bold text-lg uppercase mb-4">
              Service
            </h2>
            {service.service_guid.title && (
              <h1 className="text-4xl font-semibold capitalize">
                {service.service_guid.title}
              </h1>
            )}
          </div>
          <iframe
            className="w-full md:h-[700px] h-[300px] mt-10"
            src={service.service_guid.video}
            title="YouTube video player"
            style={{ border: "none" }} // Replaced frameborder
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          ></iframe>
        </section>
      )}

      {/* --- Related ServiceSlider --- */}
      {relativeServices.length > 0 && (
        <div className="overflow-x-hidden">
          <ServiceSlider
            data={relativeServices} // Use state variable
            heading={service.relatedServiceHeading || "Related Services"} // Provide default heading
          />
        </div>
      )}

      {/* --- Contact Form Section --- */}
      {service.contactFormInfo && (
        <div id="contact-form">
          <ContactForm
            contactFormData={service.contactFormInfo}
            // Optional: Pass selected plan if needed for contact form logic
            additionalData={{
              serviceName: service.title,
              selectedPlan: currentPlan,
            }}
          />
        </div>
      )}
    </div>
  );
}

export default ServiceDetail;
